import * as React from 'react';
import { Dropdown, Option, type DropdownProps, type OptionProps } from '@fluentui/react-components';

type SelectContextValue = {
  value?: string;
  onValueChange?: (value: string) => void;
  items: Array<{ value: string; label: React.ReactNode }>;
};

const SelectContext = React.createContext<SelectContextValue | null>(null);

export interface SelectRootProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  allowTextInput?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const Select: React.FC<SelectRootProps> = ({
  value,
  onValueChange,
  children,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const items = React.useMemo(() => {
    const result: Array<{ value: string; label: React.ReactNode }> = [];
    const walker = (nodes: React.ReactNode): React.ReactNode => {
      return React.Children.map(nodes, (child) => {
        if (!React.isValidElement(child)) return child;
        if ((child.type as { displayName?: string } | undefined)?.displayName === 'SelectItem') {
          const props = child.props as { value?: string; children?: React.ReactNode };
          result.push({ value: String(props.value), label: props.children });
          return null;
        }
        if ((child.type as { displayName?: string } | undefined)?.displayName === 'SelectContent') {
          return walker((child.props as { children?: React.ReactNode }).children);
        }
        if ((child.type as { displayName?: string } | undefined)?.displayName === 'SelectTrigger') {
          return null;
        }
        if ((child.type as { displayName?: string } | undefined)?.displayName === 'SelectValue') {
          return null;
        }
        return walker((child.props as { children?: React.ReactNode }).children);
      });
    };
    walker(children);
    return result;
  }, [children]);

  const handleChange: DropdownProps['onOptionSelect'] = (_e, data) => {
    if (data.optionValue !== undefined) {
      onValueChange?.(String(data.optionValue));
    }
  };

  const selectedItem = items.find((it) => it.value === value);
  const displayValue = selectedItem ? String(selectedItem.label) : '';

  return (
    <SelectContext.Provider value={{ value, onValueChange, items }}>
      <Dropdown
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        value={displayValue}
        selectedOptions={value ? [value] : []}
        onOptionSelect={handleChange}
      >
        {items.map((it) => (
          <Option key={it.value} value={it.value} text={String(it.label)}>
            {it.label}
          </Option>
        ))}
      </Dropdown>
    </SelectContext.Provider>
  );
};

export const SelectTrigger: React.FC<React.PropsWithChildren<{ id?: string; className?: string; 'aria-label'?: string; 'aria-labelledby'?: string }>> = () => null;
SelectTrigger.displayName = 'SelectTrigger';

export const SelectValue: React.FC<{ placeholder?: string }> = () => null;
SelectValue.displayName = 'SelectValue';

export const SelectContent: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => <>{children}</>;
SelectContent.displayName = 'SelectContent';

export interface SelectItemProps extends Pick<OptionProps, 'value'> {
  children: React.ReactNode;
}

export const SelectItem: React.FC<SelectItemProps> = () => null;
SelectItem.displayName = 'SelectItem';


